// Lectura de los datos de reasignación de una fila de `inout`.
//
// Existen TRES formatos conviviendo en la misma lista, y van a convivir mucho
// tiempo. `normalizeReassign` los aplana a uno solo para que el resto del
// código no tenga que saber de esto:
//
//   1. Cliente en 2.0.64 o anterior  -> no manda nada. Devuelve isReassigned:false.
//   2. Cliente en 2.0.65             -> manda `origin_reassigned_json`, un string.
//   3. Cliente en 2.0.68 o posterior -> manda los campos ya planos.
//
// El caso 2 NO SE PUEDE QUITAR NUNCA. Los documentos de Firestore se escriben
// con `merge:true`, así que un campo publicado una vez queda en el documento
// para siempre: los docs escritos durante la 2.0.65 van a tener ese string
// aunque el cliente ya haya actualizado. No es deuda técnica que se pague
// después, es permanente.

const EMPTY = {
  isReassigned: false,
  readPlate: "",
  assignedPlate: "",
  similarity: "",
  similarityLabel: "",
  targetTypeLabel: "",
  reassignedBy: "",
  reassignedAt: "",
  originTicketId: 0,
  ticketCanceled: false,
};

/**
 * Devuelve SIEMPRE las mismas claves, haya o no reasignación. Así el que
 * consume no necesita chequear undefined en cada campo, sólo `isReassigned`.
 */
export function normalizeReassign(row) {
  if (!row) return { ...EMPTY };

  const originId = parseInt(row.origin_ticket_id, 10) || 0;
  const canceled = row.ticket_canceled === 1 || row.ticket_canceled === true;

  // Formato 3 — campos planos (2.0.68+)
  if (row.is_reassigned) {
    return {
      isReassigned: true,
      readPlate: String(row.read_plate || "").toUpperCase(),
      assignedPlate: String(row.assigned_plate || "").toUpperCase(),
      similarity: String(row.similarity || ""),
      similarityLabel: String(row.similarity_label || ""),
      targetTypeLabel: String(row.target_type_label || ""),
      reassignedBy: String(row.reassigned_by || ""),
      reassignedAt: String(row.reassigned_at || ""),
      originTicketId: originId,
      ticketCanceled: canceled,
    };
  }

  // Formato 2 — el json crudo de la 2.0.65. Las claves son snake_case porque
  // vienen del contrato interno de Python; por eso este mapeo vive acá y en un
  // solo lugar.
  const raw = row.origin_reassigned_json || row.reassigned_json;
  if (raw) {
    let data = {};
    try {
      data = JSON.parse(raw) || {};
    } catch (e) {
      // Un json ilegible no puede hacer desaparecer el movimiento de la lista.
      data = {};
    }
    return {
      isReassigned: true,
      readPlate: String(data.read_plate || "").toUpperCase(),
      assignedPlate: String(data.target_plate || "").toUpperCase(),
      similarity: String(data.similarity || ""),
      similarityLabel: String(data.similarity_label || ""),
      targetTypeLabel: String(data.target_type_label || ""),
      reassignedBy: String(data.user_name || ""),
      reassignedAt: String(data.date || ""),
      originTicketId: originId,
      ticketCanceled: canceled,
    };
  }

  // Registros históricos backfilleados: hay ticket de origen pero no hay
  // detalle. Se sabe QUE se reasignó, no a partir de qué.
  if (originId > 0) {
    return { ...EMPTY, isReassigned: true, originTicketId: originId, ticketCanceled: canceled };
  }

  return { ...EMPTY, ticketCanceled: canceled };
}

// Nota redactada como observación, igual que en el Desktop
// (tc_qml/backend/home_slots.py). El dueño no tiene por qué saber qué es un
// "ratio de similitud": lo que necesita es saber si conviene mirar la foto.
/**
 * ¿Son la misma matrícula? Compara ignorando may/min y separadores, porque el
 * mismo vehículo aparece cargado como "GDW 462", "gdw-462" o "GDW462" según
 * quién lo haya escrito.
 */
export function samePlate(a, b) {
  const limpia = (p) => String(p || "").toUpperCase().replace(/[\s\-._]/g, "");
  const x = limpia(a);
  return !!x && x === limpia(b);
}

/**
 * De las dos matrículas de una reasignación, cuál es la que el usuario NO está
 * viendo ya en la celda.
 *
 * Hace falta porque los dos sentidos guardan cosas distintas en `plate`:
 *  - reasignación de ENTRADA, y salida asignada a un abonado o a una lista:
 *    `plate` queda con la matrícula del DESTINO, así que lo que falta mostrar
 *    es la que leyó la cámara.
 *  - salida asignada a un TICKET: `plate` queda con la que leyó la cámara, así
 *    que repetirla no aporta nada y lo que falta es a qué matrícula se asignó.
 *
 * Devuelve null cuando no hay nada nuevo que agregar: una etiqueta que repite
 * lo que está tres píxeles más arriba gasta lugar y hace dudar de si son dos
 * datos distintos.
 */
export function otherPlate(info, plateEnLaFila) {
  if (!info?.isReassigned) return null;
  if (info.readPlate && !samePlate(info.readPlate, plateEnLaFila)) {
    return { etiqueta: "la cámara leyó", plate: info.readPlate };
  }
  if (info.assignedPlate && !samePlate(info.assignedPlate, plateEnLaFila)) {
    return { etiqueta: "se asignó a", plate: info.assignedPlate };
  }
  return null;
}

const SIMILARITY_NOTES = {
  match: "",
  partial: "Diferencia compatible con un error de lectura.",
  mismatch: "La lectura no se parece a la matrícula asignada. Conviene revisar la foto.",
  no_read: "La cámara no pudo leer una matrícula, no hay con qué comparar.",
};

export function similarityNote(similarity) {
  return SIMILARITY_NOTES[similarity] || "";
}

/** true si la lectura no se parece a lo asignado: es lo único que amerita un chip. */
export function isSuspicious(similarity) {
  return similarity === "mismatch";
}

/**
 * ¿La versión instalada llega a `min`?
 *
 * Local a propósito: en este repo hay tres `compareVersions` con DOS semánticas
 * distintas (una devuelve boolean, las otras -1/0/1). Usar la equivocada acá
 * dejaría el filtro visible exactamente al revés de lo esperado, y en silencio.
 * Esta devuelve un boolean y el nombre dice cuál.
 */
export function versionGte(version, min) {
  if (!version || !min) return false;
  const a = String(version).split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(min).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return true;
}

// Primera versión que publica `is_reassigned` como campo propio de Firestore.
// Antes de ésta el filtro server-side no puede funcionar: el dato vivía dentro
// de un string y Firestore no indexa ahí adentro.
export const MIN_VERSION_REASSIGN_FILTER = "2.0.68";
